<template>
  <div class="forgot-password-view">
    <div class="logo-wrapper">
      <Logo :showText="true" :isAuthLogo="true" />
    </div>
    
    <h1 class="auth-title">Forgot Password</h1>
    
    <div class="auth-card">
      <div class="auth-form">
        <div v-if="!emailSent">
          <p class="form-description">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          
          <form @submit.prevent="requestPasswordReset">
            <div class="form-group">
              <input 
                type="email" 
                class="form-control" 
                placeholder="Your Email Address" 
                v-model="email" 
                required
              >
            </div>
            
            <button type="submit" class="btn btn-primary btn-block" :disabled="loading">
              {{ loading ? 'Sending...' : 'Send Reset Link' }}
            </button>
            
            <div class="error-message" v-if="error">{{ error }}</div>
          </form>
          
          <div class="auth-links">
            <router-link to="/login" class="back-to-login">
              ← Back to Login
            </router-link>
          </div>
        </div>
        
        <div v-else class="success-state">
          <h3>Check Your Email</h3>
          <p>We've sent password reset instructions to:</p>
          <p class="email-highlight">{{ email }}</p>
          <p class="info-text">
            The reset link will expire in 5 minutes for security reasons.
            If you don't see the email, check your spam folder.
          </p>
          
          <div class="action-buttons">
            <button @click="resendEmail" class="btn btn-secondary" :disabled="resendCooldown > 0">
              {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Email' }}
            </button>
            <router-link to="/login" class="btn btn-primary">
              Back to Login
            </router-link>
          </div>
        </div>
      </div>
    </div>
    
    <footer class="auth-footer">
      <div>© {{ currentYear }} Top Line Marketing. All Rights Reserved.</div>
    </footer>
  </div>
</template>

<script>
import Logo from '@/components/Logo.vue'
import apiService from '@/services/apiService'

export default {
  name: 'ForgotPasswordView',
  components: {
    Logo
  },
  data() {
    return {
      email: '',
      loading: false,
      error: '',
      emailSent: false,
      resendCooldown: 0
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    }
  },
  methods: {
    async requestPasswordReset() {
      if (!this.email) {
        this.error = 'Please enter your email address.';
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        await apiService.post('/business/forgot-password/', {
          email: this.email
        });
        
        this.emailSent = true;
        this.startResendCooldown();
        
      } catch (error) {
        console.error('Password reset request failed:', error);
        
        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            // Handle validation errors
            const errors = error.response.data.errors;
            if (errors.email) {
              this.error = errors.email[0];
            } else {
              this.error = 'Please check your email address and try again.';
            }
          } else if (error.response.data.detail) {
            // For rate limiting or other server messages, we'll show success
            // to avoid revealing whether the email exists
            this.emailSent = true;
            this.startResendCooldown();
          } else {
            this.error = 'An error occurred. Please try again.';
          }
        } else {
          this.error = 'Network error. Please check your connection and try again.';
        }
      } finally {
        this.loading = false;
      }
    },

    async resendEmail() {
      if (this.resendCooldown > 0) return;
      
      await this.requestPasswordReset();
    },

    startResendCooldown() {
      this.resendCooldown = 30;
      const countdown = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(countdown);
        }
      }, 1000);
    }
  }
}
</script>

<style scoped>
.forgot-password-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 20px;
  min-height: 100vh;
}

.logo-wrapper {
  margin-bottom: 20px;
}

.auth-title {
  font-size: 28px;
  font-weight: bold;
  margin: 30px 0;
  text-align: center;
  line-height: 1.3;
}

.auth-card {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 450px;
  overflow: hidden;
}

.auth-form {
  padding: 30px;
  color: #333;
}

.form-description {
  text-align: center;
  color: #666;
  margin-bottom: 25px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.form-control {
  display: block;
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  line-height: 1.5;
  color: #495057;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #4A6282;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(74, 98, 130, 0.25);
}

.btn {
  display: inline-block;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  border: 1px solid transparent;
  padding: 12px 15px;
  font-size: 16px;
  line-height: 1.5;
  border-radius: 4px;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
  text-decoration: none;
  margin: 5px;
}

.btn-block {
  width: 100%;
  margin: 5px 0;
}

.btn-primary {
  color: #fff;
  background-color: #4A6282;
  border-color: #4A6282;
}

.btn-primary:hover {
  background-color: #3d5270;
  border-color: #395069;
}

.btn-secondary {
  color: #6c757d;
  background-color: transparent;
  border-color: #6c757d;
}

.btn-secondary:hover {
  color: #fff;
  background-color: #6c757d;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.error-message {
  color: #e94057;
  margin-top: 10px;
  font-size: 14px;
  text-align: center;
}

.auth-links {
  margin-top: 20px;
  text-align: center;
}

.back-to-login {
  color: #4A6282;
  text-decoration: none;
  font-size: 14px;
}

.back-to-login:hover {
  text-decoration: underline;
}

.success-state {
  text-align: center;
}

.success-icon {
  font-size: 48px;
  color: #28a745;
  margin-bottom: 20px;
}

.success-state h3 {
  color: #333;
  margin-bottom: 15px;
}

.success-state p {
  color: #666;
  margin-bottom: 10px;
}

.email-highlight {
  font-weight: bold;
  color: #4A6282;
  font-size: 16px;
}

.info-text {
  font-size: 14px;
  line-height: 1.4;
  margin: 20px 0;
}

.action-buttons {
  margin-top: 30px;
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.auth-footer {
  margin-top: 30px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
}

@media (max-width: 480px) {
  .action-buttons {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
    margin: 5px 0;
  }
}
</style>
